// src/feature-module/admin/AuditLogs.tsx
//
// Platform audit-log viewer. Filtering and paging are server-side: the endpoint
// used to return the whole ActivityLog table and this page sliced it in the
// browser, which stopped being viable the moment the query was paginated.
//
// The Request ID field is the one that matters for support. A tenant reporting a
// failure now has an id on the error they saw; pasting it here returns every
// audit row that request produced.

import React, { useMemo, useState } from "react";
import { Card, Table, Tag, Select, Input, Space, DatePicker, Button, Tooltip, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import {
  useGetAuditLogsQuery,
  useGetTenantAuditLogsQuery,
  type AuditQuery,
} from "../../core/redux/services/adminAuditApi";
import { useGetTenantsQuery } from "../../core/redux/services/adminTenantApi";

const txt = (v: any) => (v == null || v === "" ? "—" : String(v));
const dateTime = (v: any) => (v ? new Date(v).toLocaleString() : "—");

const actionColor = (a = "") => {
  if (/DELETE|REVOKE|REMOVE|DEACTIVATE|DENIED/i.test(a)) return "red";
  if (/CREATE|ADD|ASSIGN|ACTIVATE|INVITE/i.test(a)) return "green";
  if (/UPDATE|EDIT|CHANGE|TOGGLE/i.test(a)) return "blue";
  if (/LOGIN|LOGOUT|AUTH/i.test(a)) return "purple";
  return "default";
};

const AuditLogs: React.FC = () => {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [requestId, setRequestId] = useState("");
  const [action, setAction] = useState("");
  const [module, setModule] = useState("");
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  const { data: tenants = [] } = useGetTenantsQuery();

  // Sent to the server on every change; `module`/`action` are matched
  // case-insensitively there, which matters because the trail carries both
  // `pos` and `POS` from different call sites.
  const filters: AuditQuery = useMemo(
    () => ({
      page,
      limit,
      requestId: requestId.trim() || undefined,
      action: action.trim() || undefined,
      module: module.trim() || undefined,
      dateFrom: range?.[0]?.toISOString(),
      dateTo: range?.[1]?.toISOString(),
    }),
    [page, limit, requestId, action, module, range],
  );

  const all = useGetAuditLogsQuery(filters, { skip: !!tenantId });
  const scoped = useGetTenantAuditLogsQuery({ tenantId: tenantId as string, ...filters }, { skip: !tenantId });

  const active = tenantId ? scoped : all;
  const rows = active.data?.data ?? [];
  const meta = active.data?.meta;
  const loading = active.isFetching;

  // Any filter change invalidates the current page number.
  const onFilter = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setPage(1);
  };

  const columns = [
    { title: "Time", dataIndex: "timestamp", render: (v: any, r: any) => dateTime(v || r.createdAt), width: 175 },
    {
      title: "User",
      key: "user",
      ellipsis: true,
      render: (_: any, r: any) => txt(r.user?.name || r.user?.email || r.userId),
    },
    { title: "Action", dataIndex: "action", render: (a: string) => <Tag color={actionColor(a)}>{txt(a)}</Tag> },
    { title: "Module", dataIndex: "module", render: txt },
    { title: "Entity", dataIndex: "entityId", ellipsis: true, render: txt },
    { title: "Tenant", dataIndex: "tenantId", ellipsis: true, render: txt },
    {
      title: "Request",
      dataIndex: "requestId",
      width: 130,
      render: (v: any) =>
        v ? (
          <Tooltip title={`${v} — click to filter to this request`}>
            <Typography.Link onClick={() => { setRequestId(v); setPage(1); }} code>
              {String(v).slice(0, 8)}…
            </Typography.Link>
          </Tooltip>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    { title: "IP", dataIndex: "ipAddress", render: txt },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h4 className="mb-1">Audit Logs</h4>
          <p className="text-muted mb-0">Platform-wide activity trail</p>
        </div>
        <Card>
          <Space wrap className="mb-3">
            <Select
              showSearch
              allowClear
              style={{ minWidth: 220 }}
              placeholder="All tenants"
              optionFilterProp="label"
              value={tenantId}
              onChange={onFilter(setTenantId)}
              options={tenants.map((t: any) => ({ value: t.id, label: t.name || t.id }))}
            />
            <Input
              allowClear
              style={{ width: 300 }}
              placeholder="Request ID (from the error the tenant saw)"
              value={requestId}
              onChange={(e) => onFilter(setRequestId)(e.target.value)}
            />
            <Input
              allowClear
              style={{ width: 170 }}
              placeholder="Action"
              value={action}
              onChange={(e) => onFilter(setAction)(e.target.value)}
            />
            <Input
              allowClear
              style={{ width: 150 }}
              placeholder="Module"
              value={module}
              onChange={(e) => onFilter(setModule)(e.target.value)}
            />
            <DatePicker.RangePicker showTime value={range as any} onChange={onFilter(setRange) as any} />
            <Button icon={<ReloadOutlined />} onClick={() => active.refetch()} loading={loading}>
              Refresh
            </Button>
          </Space>

          <Table
            rowKey={(r: any) => r.id ?? JSON.stringify(r).slice(0, 40)}
            columns={columns}
            dataSource={rows}
            loading={loading}
            size="small"
            scroll={{ x: "max-content" }}
            pagination={{
              current: meta?.page ?? page,
              pageSize: meta?.limit ?? limit,
              total: meta?.total ?? 0,
              showSizeChanger: true,
              showTotal: (t) => `${t} entries`,
              onChange: (p, ps) => {
                setPage(p);
                if (ps !== limit) setLimit(ps);
              },
            }}
            expandable={{
              expandedRowRender: (r: any) => (
                <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontSize: 12 }}>
                  {JSON.stringify(r.details ?? r.metadata ?? {}, null, 2)}
                </pre>
              ),
              rowExpandable: (r: any) => Boolean(r.details || r.metadata),
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default AuditLogs;
